/**
 * In-memory community DB mirroring admin-community-api-serverless behavior.
 * Covers threads, comments, votes, likes (deprecated), reports, views, boards,
 * tags, and per-user read-distance settings.
 */

import { DEFAULT_READ_DISTANCE_M } from './seedUserSettings';
import { getExpandedMockSeeds } from './expandMockSeeds';
import {
  encodeGeohash,
  haversineM,
  prefixForRadiusM,
  toRFC3339UTC,
  utcDateString,
} from './geohashUtil';

export const REPORT_STATUS_OPEN = 0;

const READ_DISTANCE_MIN = 1000;
const READ_DISTANCE_MAX = 10000;
const TAG_MAX = 5;
const TAG_PATTERN = /^[0-9A-Za-z_]+$/;
const TRENDING_DOWN_THRESHOLD = 10;

function clone(obj) {
  return JSON.parse(JSON.stringify(obj));
}

function nextId(list) {
  return list.reduce((m, r) => Math.max(m, r.id || 0), 0) + 1;
}

function err(msg, code = -1, status = 400) {
  const e = new Error(msg);
  e.code = code;
  e.status = status;
  throw e;
}

function activeThread(t, includeDeleted) {
  if (!includeDeleted && t.deleted_at) return false;
  return true;
}

function fakeAuthor(userId) {
  if (userId == null) return null;
  return {
    user_id: userId,
    username: `user${userId}`,
    profile_photo_url: null,
  };
}

function toCommentJSON(c) {
  return {
    id: c.id,
    thread_id: c.thread_id,
    parent_comment_id: c.parent_comment_id,
    depth: c.depth,
    author_user_id: c.author_user_id,
    body: c.body,
    created_at: c.created_at,
    updated_at: c.updated_at,
    edited_at: c.edited_at,
  };
}

function boardJSON(b) {
  if (!b) return null;
  const { thread_ids, ...rest } = b;
  return clone(rest);
}

function boardBrief(b) {
  if (!b) return null;
  return { id: b.id, board_key: b.board_key, name: b.name };
}

function validateTags(tags) {
  if (!Array.isArray(tags)) return [];
  if (tags.length > TAG_MAX) err(`at most ${TAG_MAX} tags allowed`);
  const out = [];
  tags.forEach((raw) => {
    const tag = String(raw).trim();
    if (!tag) return;
    if (tag.length > 50 || !TAG_PATTERN.test(tag)) err(`invalid tag: ${tag}`);
    if (!out.includes(tag)) out.push(tag);
  });
  return out;
}

function normalizeBoardName(name) {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) err('board name is required');
  if (trimmed.length > 120) err('board name too long');
  if (/\s/.test(trimmed)) err('board name must not contain whitespace');
  return trimmed;
}

class CommunityMockStore {
  constructor() {
    this.reset();
  }

  reset() {
    const expanded = getExpandedMockSeeds();
    this.threads = clone(expanded.threads);
    this.comments = clone(expanded.comments);
    this.likes = clone(expanded.likes);
    this.reports = clone(expanded.reports);
    this.viewDedup = clone(expanded.viewDedup);
    this.votes = clone(expanded.votes);
    this.threadTags = clone(expanded.threadTags);
    this.boards = clone(expanded.boards);
    this.userSettings = { ...expanded.userSettings };

    // Ensure every thread carries the newer fields.
    this.threads.forEach((t) => {
      t.up_count = 0;
      t.down_count = 0;
      t.board_id = null;
      t.tags = [];
    });

    const threadById = new Map(this.threads.map((t) => [t.id, t]));

    // Wire tags onto threads.
    this.threadTags.forEach((row) => {
      const t = threadById.get(row.thread_id);
      if (t && !t.tags.includes(row.tag)) t.tags.push(row.tag);
    });

    // Wire votes onto thread counts.
    this.votes.forEach((v) => {
      const t = threadById.get(v.thread_id);
      if (!t) return;
      if (v.value === 1) t.up_count += 1;
      else if (v.value === -1) t.down_count += 1;
    });

    // Wire boards onto threads and compute thread_count.
    this.boards.forEach((b) => {
      const ids = b.thread_ids || [];
      let count = 0;
      ids.forEach((tid) => {
        const t = threadById.get(tid);
        if (t && !t.deleted_at) {
          t.board_id = b.id;
          count += 1;
        }
      });
      b.thread_count = count;
    });

    this._threadIdSeq = nextId(this.threads) - 1;
    this._commentIdSeq = nextId(this.comments) - 1;
    this._reportIdSeq = nextId(this.reports) - 1;
    this._boardIdSeq = nextId(this.boards) - 1;
  }

  /* ---- internal helpers ---- */

  _findThread(id, includeDeleted = false) {
    const t = this.threads.find((x) => x.id === Number(id));
    if (!t || !activeThread(t, includeDeleted)) err('thread not found', -601, 404);
    return t;
  }

  _findBoard(id) {
    const b = this.boards.find((x) => x.id === Number(id));
    if (!b) err('board not found', -670, 404);
    return b;
  }

  _myVote(threadId, userId) {
    if (userId == null) return null;
    const v = this.votes.find(
      (x) => x.thread_id === Number(threadId) && x.user_id === Number(userId),
    );
    return v ? v.value : null;
  }

  _toListItem(t, extra = {}) {
    const item = clone(t);
    if (t.board_id) {
      const b = this.boards.find((x) => x.id === t.board_id);
      item.board = b ? boardBrief(b) : null;
    }
    item.username = extra.username ?? `user${t.author_user_id}`;
    if (extra.distance_m != null) item.distance_m = extra.distance_m;
    return item;
  }

  /* ---- Threads CRUD ---- */

  getThread(id, includeDeleted = false, actingUserId = null) {
    const t = this._findThread(id, includeDeleted);
    const detail = clone(t);
    detail.author = fakeAuthor(t.author_user_id);
    detail.board = t.board_id ? boardBrief(this.boards.find((b) => b.id === t.board_id)) : null;
    detail.my_vote = this._myVote(t.id, actingUserId);
    return detail;
  }

  postThread(payload, actingUserId) {
    const author = Number(payload.author_user_id ?? actingUserId);
    if (!author) err('author_user_id required');
    const kind = payload.kind || 'general';
    if (!['general', 'regional'].includes(kind)) err('invalid kind');

    const lat = payload.latitude != null && payload.latitude !== '' ? Number(payload.latitude) : null;
    const lon = payload.longitude != null && payload.longitude !== '' ? Number(payload.longitude) : null;
    const hasCoords = lat != null && lon != null && !Number.isNaN(lat) && !Number.isNaN(lon);

    if (kind === 'regional' && !hasCoords) {
      err('regional requires latitude and longitude');
    }
    if (!String(payload.body ?? '').trim()) err('body required');

    const tags = validateTags(payload.tags);

    // Resolve board attachment (board_id, or get-or-create by board_name).
    let boardId = null;
    if (payload.board_id != null && payload.board_id !== '') {
      boardId = this._findBoard(payload.board_id).id;
    } else if (payload.board_name) {
      boardId = this._getOrCreateBoard(payload.board_name, author, payload.planet_id, lat, lon).id;
    }

    const id = ++this._threadIdSeq;
    const now = toRFC3339UTC(new Date());
    const geohash = hasCoords ? encodeGeohash(lat, lon) : null;

    const row = {
      id,
      author_user_id: author,
      planet_id: payload.planet_id != null && payload.planet_id !== '' ? Number(payload.planet_id) : null,
      board_id: boardId,
      kind,
      has_gps: hasCoords ? 1 : 0,
      gps_verified: payload.gps_verified ? 1 : 0,
      arrival_based: payload.arrival_based ? 1 : 0,
      title: payload.title ?? null,
      body: payload.body,
      tags,
      latitude: lat,
      longitude: lon,
      geohash,
      view_count: 0,
      like_count: 0,
      up_count: 0,
      down_count: 0,
      comment_count: 0,
      deleted_at: null,
      created_at: now,
      updated_at: now,
      edited_at: null,
    };
    this.threads.unshift(row);
    tags.forEach((tag) => this.threadTags.push({ thread_id: id, tag }));
    if (boardId) {
      const b = this.boards.find((x) => x.id === boardId);
      if (b) b.thread_count = (b.thread_count || 0) + 1;
    }
    return this.getThread(id);
  }

  patchThread(id, payload, _actingUserId, skipAuthorCheck = true) {
    const t = this._findThread(id);
    if (!skipAuthorCheck && payload.acting_user_id && t.author_user_id !== Number(payload.acting_user_id)) {
      err('forbidden', -603, 403);
    }

    if (payload.title !== undefined) t.title = payload.title;
    if (payload.body !== undefined) t.body = payload.body;
    if (payload.planet_id !== undefined) {
      t.planet_id = payload.planet_id === '' || payload.planet_id == null ? null : Number(payload.planet_id);
    }
    if (payload.gps_verified !== undefined) t.gps_verified = payload.gps_verified ? 1 : 0;
    if (payload.arrival_based !== undefined) t.arrival_based = payload.arrival_based ? 1 : 0;

    if (payload.latitude != null && payload.latitude !== '' && payload.longitude != null && payload.longitude !== '') {
      t.latitude = Number(payload.latitude);
      t.longitude = Number(payload.longitude);
      t.geohash = encodeGeohash(t.latitude, t.longitude);
      t.has_gps = 1;
    }

    if (payload.tags !== undefined) {
      const tags = validateTags(payload.tags);
      t.tags = tags;
      this.threadTags = this.threadTags.filter((r) => r.thread_id !== t.id);
      tags.forEach((tag) => this.threadTags.push({ thread_id: t.id, tag }));
    }

    if (payload.board_id !== undefined || payload.board_name !== undefined) {
      this._reassignBoard(t, payload);
    }

    t.updated_at = toRFC3339UTC(new Date());
    t.edited_at = t.updated_at;
    return this.getThread(t.id);
  }

  deleteThread(id, _actingUserId, skipAuthorCheck = true) {
    const t = this._findThread(id);
    if (!skipAuthorCheck) err('forbidden', -603, 403);
    t.deleted_at = toRFC3339UTC(new Date());
    t.updated_at = t.deleted_at;
    if (t.board_id) {
      const b = this.boards.find((x) => x.id === t.board_id);
      if (b && b.thread_count > 0) b.thread_count -= 1;
    }
    return { ok: true, id: t.id };
  }

  /* ---- Feeds & discovery ---- */

  _paginate(rows, before_id, limit, defLimit = 20) {
    rows.sort((a, b) => b.id - a.id);
    if (before_id) {
      const bid = Number(before_id);
      rows = rows.filter((t) => t.id < bid);
    }
    const lim = Math.min(Math.max(1, Number(limit) || defLimit), 100);
    const slice = rows.slice(0, lim).map((t) => this._toListItem(t));
    const nextBefore = slice.length === lim && slice.length > 0 ? slice[slice.length - 1].id : null;
    return { data: slice, meta: { next_before_id: nextBefore } };
  }

  getThreadsFeed({ kind, before_id, limit = 20, search, include_deleted = false, planet_id } = {}) {
    let rows = this.threads.filter((t) => activeThread(t, include_deleted));
    if (kind) rows = rows.filter((t) => t.kind === kind);
    if (planet_id != null && planet_id !== '') {
      const pid = Number(planet_id);
      rows = rows.filter((t) => t.planet_id === pid);
    }
    if (search) {
      const q = search.toLowerCase();
      rows = rows.filter(
        (t) => (t.title && t.title.toLowerCase().includes(q)) || t.body.toLowerCase().includes(q),
      );
    }
    return this._paginate(rows, before_id, limit);
  }

  getThreadsGlobal({ before_id, limit = 20 } = {}) {
    const rows = this.threads.filter((t) => !t.deleted_at);
    return this._paginate(rows, before_id, limit);
  }

  getMyThreads({ before_id, limit = 20 } = {}, actingUserId) {
    const uid = Number(actingUserId);
    const rows = this.threads.filter((t) => !t.deleted_at && t.author_user_id === uid);
    return this._paginate(rows, before_id, limit);
  }

  getThreadsGps({ before_id, limit = 20 } = {}) {
    const rows = this.threads.filter((t) => !t.deleted_at && t.has_gps === 1);
    return this._paginate(rows, before_id, limit);
  }

  getThreadsByTag({ tag, before_id, limit = 20 } = {}) {
    const prefix = String(tag ?? '').trim().toLowerCase();
    if (!prefix) err('tag is required');
    const rows = this.threads.filter(
      (t) =>
        !t.deleted_at &&
        Array.isArray(t.tags) &&
        t.tags.some((tg) => String(tg).toLowerCase().startsWith(prefix)),
    );
    return this._paginate(rows, before_id, limit);
  }

  getThreadsTrending({ page = 0, limit = 20 } = {}) {
    const rows = this.threads
      .filter((t) => !t.deleted_at && (t.down_count || 0) < TRENDING_DOWN_THRESHOLD)
      .sort((a, b) => {
        if ((b.up_count || 0) !== (a.up_count || 0)) return (b.up_count || 0) - (a.up_count || 0);
        return new Date(b.created_at) - new Date(a.created_at);
      });
    const p = Math.max(0, Number(page) || 0);
    const lim = Math.min(Math.max(1, Number(limit) || 20), 100);
    const start = p * lim;
    const slice = rows.slice(start, start + lim).map((t) => this._toListItem(t));
    const nextPage = start + lim < rows.length ? p + 1 : null;
    return { data: slice, meta: { next_page: nextPage } };
  }

  getThreadsNearby({ latitude, longitude, radius_m, planet_id, limit = 20 } = {}, actingUserId) {
    const lat = Number(latitude);
    const lon = Number(longitude);
    if (Number.isNaN(lat) || Number.isNaN(lon)) err('latitude and longitude required');

    const radius = radius_m != null && radius_m !== ''
      ? Number(radius_m)
      : this.getReadDistance(actingUserId);
    const prefixLen = prefixForRadiusM(radius);

    let rows = this.threads.filter(
      (t) =>
        !t.deleted_at &&
        t.kind === 'regional' &&
        t.has_gps &&
        t.geohash &&
        t.latitude != null &&
        t.longitude != null,
    );

    if (planet_id != null && planet_id !== '') {
      const pid = Number(planet_id);
      rows = rows.filter((t) => t.planet_id === pid);
    }

    const prefix = encodeGeohash(lat, lon).slice(0, prefixLen);
    rows = rows.filter((t) => t.geohash.startsWith(prefix));

    const withDist = rows
      .map((t) => {
        const distance_m = haversineM(lat, lon, t.latitude, t.longitude);
        return this._toListItem(t, { distance_m });
      })
      .filter((t) => t.distance_m <= radius)
      .sort((a, b) => a.distance_m - b.distance_m);

    const lim = Math.min(Math.max(1, Number(limit) || 20), 100);
    return { data: withDist.slice(0, lim), meta: {} };
  }

  postNearbyUnreadCount({ latitude, longitude, last_seen_at, planet_id } = {}, actingUserId) {
    const lat = Number(latitude);
    const lon = Number(longitude);
    if (Number.isNaN(lat) || Number.isNaN(lon)) err('latitude and longitude required');
    if (!last_seen_at) err('last_seen_at is required');
    const since = new Date(last_seen_at).getTime();
    if (Number.isNaN(since)) err('invalid last_seen_at');

    const radius = this.getReadDistance(actingUserId);
    const prefixLen = prefixForRadiusM(radius);
    const prefix = encodeGeohash(lat, lon).slice(0, prefixLen);

    let rows = this.threads.filter(
      (t) =>
        !t.deleted_at &&
        t.kind === 'regional' &&
        t.has_gps &&
        t.geohash &&
        t.geohash.startsWith(prefix) &&
        new Date(t.created_at).getTime() > since,
    );
    if (planet_id != null && planet_id !== '') {
      const pid = Number(planet_id);
      rows = rows.filter((t) => t.planet_id === pid);
    }
    rows = rows.filter((t) => haversineM(lat, lon, t.latitude, t.longitude) <= radius);

    return { unread_count: rows.length, radius_m: radius, last_seen_at };
  }

  /* ---- Comments ---- */

  getComments(threadId, { before_id, limit = 50 } = {}) {
    this._findThread(threadId);
    let rows = this.comments.filter((c) => c.thread_id === Number(threadId) && !c.deleted_at);
    rows.sort((a, b) => b.id - a.id);
    if (before_id) rows = rows.filter((c) => c.id < Number(before_id));
    const lim = Math.min(Math.max(1, Number(limit) || 50), 100);
    const slice = rows.slice(0, lim).map(toCommentJSON);
    const nextBefore = slice.length === lim && slice.length > 0 ? slice[slice.length - 1].id : null;
    return { data: slice, meta: { next_before_id: nextBefore } };
  }

  postComment(threadId, payload, actingUserId) {
    this._findThread(threadId);
    if (payload.parent_comment_id != null && payload.parent_comment_id !== '') {
      err('nested comments not supported', -1, 400);
    }
    const author = Number(payload.author_user_id ?? actingUserId);
    if (!author) err('author_user_id required');
    if (!payload.body?.trim()) err('body required');

    const id = ++this._commentIdSeq;
    const now = toRFC3339UTC(new Date());
    const row = {
      id,
      thread_id: Number(threadId),
      parent_comment_id: null,
      depth: 1,
      author_user_id: author,
      body: payload.body.trim(),
      deleted_at: null,
      created_at: now,
      updated_at: now,
      edited_at: null,
    };
    this.comments.push(row);
    const t = this.threads.find((x) => x.id === Number(threadId));
    if (t) t.comment_count += 1;
    return toCommentJSON(row);
  }

  patchComment(commentId, payload, _actingUserId, skipAuthorCheck = true) {
    const c = this.comments.find((x) => x.id === Number(commentId));
    if (!c || c.deleted_at) err('comment not found', -602, 404);
    if (!skipAuthorCheck && payload.acting_user_id && c.author_user_id !== Number(payload.acting_user_id)) {
      err('forbidden', -604, 403);
    }
    if (!payload.body?.trim()) err('body required');
    c.body = payload.body.trim();
    c.updated_at = toRFC3339UTC(new Date());
    c.edited_at = c.updated_at;
    return toCommentJSON(c);
  }

  deleteComment(commentId, _actingUserId, skipAuthorCheck = true) {
    const c = this.comments.find((x) => x.id === Number(commentId));
    if (!c || c.deleted_at) err('comment not found', -602, 404);
    if (!skipAuthorCheck) err('forbidden', -604, 403);
    c.deleted_at = toRFC3339UTC(new Date());
    const t = this.threads.find((x) => x.id === c.thread_id);
    if (t && t.comment_count > 0) t.comment_count -= 1;
    return { ok: true, id: c.id };
  }

  /* ---- Votes ---- */

  postVote(threadId, value, userId) {
    const t = this._findThread(threadId);
    const uid = Number(userId);
    if (!uid) err('user id required');
    const v = value === 'up' || value === 1 ? 1 : value === 'down' || value === -1 ? -1 : null;
    if (v == null) err('value must be up or down');

    const existing = this.votes.find((x) => x.thread_id === t.id && x.user_id === uid);
    if (existing) {
      if (existing.value !== v) {
        if (existing.value === 1) t.up_count = Math.max(0, t.up_count - 1);
        else t.down_count = Math.max(0, t.down_count - 1);
        existing.value = v;
        if (v === 1) t.up_count += 1;
        else t.down_count += 1;
      }
    } else {
      this.votes.push({ thread_id: t.id, user_id: uid, value: v, created_at: toRFC3339UTC(new Date()) });
      if (v === 1) t.up_count += 1;
      else t.down_count += 1;
    }
    return { ok: true, thread_id: t.id, value: v, up_count: t.up_count, down_count: t.down_count };
  }

  deleteVote(threadId, userId) {
    const t = this._findThread(threadId);
    const uid = Number(userId);
    const idx = this.votes.findIndex((x) => x.thread_id === t.id && x.user_id === uid);
    if (idx < 0) err('vote not found', -651, 404);
    const { value } = this.votes[idx];
    this.votes.splice(idx, 1);
    if (value === 1) t.up_count = Math.max(0, t.up_count - 1);
    else if (value === -1) t.down_count = Math.max(0, t.down_count - 1);
    return { ok: true, thread_id: t.id };
  }

  /* ---- Likes (deprecated) ---- */

  postLike(threadId, userId) {
    this._findThread(threadId);
    const uid = Number(userId);
    if (this.likes.some((l) => l.thread_id === Number(threadId) && l.user_id === uid)) {
      err('already liked', -642, 409);
    }
    this.likes.push({ thread_id: Number(threadId), user_id: uid, created_at: toRFC3339UTC(new Date()) });
    const t = this.threads.find((x) => x.id === Number(threadId));
    if (t) t.like_count += 1;
    return { ok: true, thread_id: Number(threadId) };
  }

  deleteLike(threadId, userId) {
    const idx = this.likes.findIndex(
      (l) => l.thread_id === Number(threadId) && l.user_id === Number(userId),
    );
    if (idx < 0) err('like not found', -420, 404);
    this.likes.splice(idx, 1);
    const t = this.threads.find((x) => x.id === Number(threadId));
    if (t && t.like_count > 0) t.like_count -= 1;
    return { ok: true, thread_id: Number(threadId) };
  }

  getLikesForThread(threadId) {
    return this.likes.filter((l) => l.thread_id === Number(threadId)).map(clone);
  }

  /* ---- Reports ---- */

  postReport(threadId, payload, reporterUserId) {
    this._findThread(threadId);
    const reporter = Number(payload.reporter_user_id ?? reporterUserId);
    if (!reporter) err('reporter_user_id required');
    const reportType = Number(payload.report_type);
    if (!reportType || reportType < 1 || reportType > 999) err('report_type must be 1-999');
    if (payload.detail && String(payload.detail).length > 2000) err('detail too long');

    const dup = this.reports.find(
      (r) =>
        r.thread_id === Number(threadId) &&
        r.reporter_user_id === reporter &&
        r.status === REPORT_STATUS_OPEN,
    );
    if (dup) err('open report exists', -643, 409);

    const id = ++this._reportIdSeq;
    const row = {
      id,
      thread_id: Number(threadId),
      reporter_user_id: reporter,
      report_type: reportType,
      detail: payload.detail ?? null,
      status: REPORT_STATUS_OPEN,
      created_at: toRFC3339UTC(new Date()),
    };
    this.reports.push(row);
    return { ok: true, thread_id: Number(threadId) };
  }

  getReports({ status, thread_id, limit = 50 } = {}) {
    let rows = [...this.reports];
    if (status != null && status !== '') rows = rows.filter((r) => r.status === Number(status));
    if (thread_id) rows = rows.filter((r) => r.thread_id === Number(thread_id));
    rows.sort((a, b) => b.id - a.id);
    const lim = Math.min(Number(limit) || 50, 200);
    return { data: rows.slice(0, lim).map(clone), meta: {} };
  }

  /* ---- Views ---- */

  postThreadView(threadId, userId) {
    this._findThread(threadId);
    const uid = Number(userId);
    const bucket = utcDateString();
    const exists = this.viewDedup.some(
      (v) => v.thread_id === Number(threadId) && v.user_id === uid && v.bucket_date === bucket,
    );
    if (!exists) {
      this.viewDedup.push({ thread_id: Number(threadId), user_id: uid, bucket_date: bucket });
      const t = this.threads.find((x) => x.id === Number(threadId));
      if (t) t.view_count += 1;
    }
    const t = this.threads.find((x) => x.id === Number(threadId));
    return { thread_id: Number(threadId), view_counted: !exists, view_count: t ? t.view_count : 0 };
  }

  /* ---- Boards ---- */

  _getOrCreateBoard(name, createdBy, planetId, lat, lon) {
    const normalized = normalizeBoardName(name);
    const key = normalized.toLowerCase();
    const existing = this.boards.find((b) => b.board_key === key);
    if (existing) return existing;
    return this._createBoard(normalized, createdBy, planetId, lat, lon);
  }

  _createBoard(name, createdBy, planetId, lat, lon) {
    const id = ++this._boardIdSeq;
    const latN = lat != null && lat !== '' ? Number(lat) : null;
    const lonN = lon != null && lon !== '' ? Number(lon) : null;
    const board = {
      id,
      board_key: name.toLowerCase(),
      name,
      planet_id: planetId != null && planetId !== '' ? Number(planetId) : null,
      created_by_user_id: Number(createdBy) || null,
      latitude: latN,
      longitude: lonN,
      geohash: latN != null && lonN != null ? encodeGeohash(latN, lonN) : null,
      hidden: 0,
      created_at: toRFC3339UTC(new Date()),
      thread_count: 0,
      thread_ids: [],
    };
    this.boards.push(board);
    return board;
  }

  _reassignBoard(thread, payload) {
    if (thread.board_id) {
      const old = this.boards.find((b) => b.id === thread.board_id);
      if (old && old.thread_count > 0) old.thread_count -= 1;
    }
    let next = null;
    if (payload.board_id != null && payload.board_id !== '') {
      next = this._findBoard(payload.board_id);
    } else if (payload.board_name) {
      next = this._getOrCreateBoard(payload.board_name, thread.author_user_id, thread.planet_id, thread.latitude, thread.longitude);
    }
    thread.board_id = next ? next.id : null;
    if (next) next.thread_count = (next.thread_count || 0) + 1;
  }

  postBoard(payload, actingUserId) {
    const name = normalizeBoardName(payload.name);
    const key = name.toLowerCase();
    if (this.boards.some((b) => b.board_key === key)) err('board already exists', -671, 409);
    const board = this._createBoard(
      name,
      payload.created_by_user_id ?? actingUserId,
      payload.planet_id,
      payload.latitude,
      payload.longitude,
    );
    return boardJSON(board);
  }

  getBoards({ latitude, longitude, radius_m, planet_id, limit = 20 } = {}) {
    let rows = this.boards.filter((b) => b.hidden !== 1);
    if (planet_id != null && planet_id !== '') {
      const pid = Number(planet_id);
      rows = rows.filter((b) => b.planet_id === pid);
    }
    const lat = latitude != null && latitude !== '' ? Number(latitude) : null;
    const lon = longitude != null && longitude !== '' ? Number(longitude) : null;
    if (lat != null && lon != null && !Number.isNaN(lat) && !Number.isNaN(lon)) {
      const radius = radius_m != null && radius_m !== '' ? Number(radius_m) : DEFAULT_READ_DISTANCE_M;
      const prefix = encodeGeohash(lat, lon).slice(0, prefixForRadiusM(radius));
      rows = rows.filter((b) => !b.geohash || b.geohash.startsWith(prefix));
    }
    rows.sort((a, b) => (b.thread_count || 0) - (a.thread_count || 0));
    const lim = Math.min(Math.max(1, Number(limit) || 20), 100);
    return { data: rows.slice(0, lim).map(boardJSON), meta: {} };
  }

  getBoardById(boardId) {
    return boardJSON(this._findBoard(boardId));
  }

  getThreadsByBoard(boardId, { before_id, limit = 20 } = {}) {
    const b = this._findBoard(boardId);
    const rows = this.threads.filter((t) => !t.deleted_at && t.board_id === b.id);
    return this._paginate(rows, before_id, limit);
  }

  /* ---- Settings ---- */

  getReadDistance(userId) {
    if (userId == null) return DEFAULT_READ_DISTANCE_M;
    return this.userSettings[Number(userId)] ?? DEFAULT_READ_DISTANCE_M;
  }

  updateReadDistance({ read_distance_m } = {}, actingUserId) {
    const val = Number(read_distance_m);
    if (Number.isNaN(val) || val < READ_DISTANCE_MIN || val > READ_DISTANCE_MAX) {
      err(`read_distance_m must be ${READ_DISTANCE_MIN}-${READ_DISTANCE_MAX}`);
    }
    if (actingUserId != null) this.userSettings[Number(actingUserId)] = val;
    return { read_distance_m: val };
  }

  /* ---- Aggregates ---- */

  getOverviewStats() {
    const active = this.threads.filter((t) => !t.deleted_at);
    const regional = active.filter((t) => t.kind === 'regional').length;
    const general = active.filter((t) => t.kind === 'general').length;
    const gpsThreads = active.filter((t) => t.has_gps === 1).length;
    const openReports = this.reports.filter((r) => r.status === REPORT_STATUS_OPEN).length;
    const totalViews = active.reduce((s, t) => s + t.view_count, 0);
    const totalLikes = active.reduce((s, t) => s + t.like_count, 0);
    const totalUp = active.reduce((s, t) => s + (t.up_count || 0), 0);
    const totalDown = active.reduce((s, t) => s + (t.down_count || 0), 0);
    const taggedThreads = active.filter((t) => Array.isArray(t.tags) && t.tags.length > 0).length;
    const boardThreads = active.filter((t) => t.board_id != null).length;
    return {
      total_threads: active.length,
      regional_count: regional,
      general_count: general,
      gps_count: gpsThreads,
      open_reports: openReports,
      total_views: totalViews,
      total_likes: totalLikes,
      total_up: totalUp,
      total_down: totalDown,
      comment_count: this.comments.filter((c) => !c.deleted_at).length,
      board_count: this.boards.filter((b) => b.hidden !== 1).length,
      board_thread_count: boardThreads,
      tagged_thread_count: taggedThreads,
      vote_count: this.votes.length,
      partial: false,
    };
  }

  getRegionalThreadsForMap() {
    return this.threads
      .filter(
        (t) => !t.deleted_at && t.kind === 'regional' && t.latitude != null && t.longitude != null,
      )
      .map((t) => this._toListItem(t));
  }
}

export const communityMockStore = new CommunityMockStore();
